/**
 * A list of hymn references, used by every secondary page (tune, person, meter).
 * Shared so the three pages cannot drift in how a hymn reads.
 */
import { Link, useLocation } from 'react-router-dom'
import type { HymnRef } from '@gjl/music-manager/hymnal-pages'
import { Breadcrumbs } from '@gjl/studio-chrome'

import { HYMNAL_1982 } from '@/data/hymnal'

export function HymnList({ hymns, columns = true }: { hymns: HymnRef[]; columns?: boolean }) {
  if (hymns.length === 0) return null
  return (
    <ul className={columns ? 'grid sm:grid-cols-2 gap-x-6' : ''}>
      {hymns.map((h) => (
        <li key={h.slug} className="py-1 text-sm">
          <Link to={`/hymnal/${HYMNAL_1982.id}/hymn/${h.slug}`} className="group flex gap-3">
            <span className="font-mono text-xs text-muted-foreground pt-0.5 w-9 shrink-0 text-right">{h.number}</span>
            <span className="text-muted-foreground group-hover:text-foreground">{h.title}</span>
          </Link>
        </li>
      ))}
    </ul>
  )
}

/** Section heading used across the secondary pages. */
export function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">{children}</h2>
}

/**
 * Breadcrumb trail. Renders the marketing site's own `<Breadcrumbs>` so the
 * trail is identical on both sides of the bundler boundary — and so its leading
 * Home crumb points at `/`, which is a reader's first step out of the reference
 * and into the site it is meant to introduce.
 */
export function Crumb({ label }: { label: string }) {
  const { pathname } = useLocation()
  return (
    <Breadcrumbs
      className="mb-6"
      items={[
        { name: HYMNAL_1982.label, url: `/hymnal/${HYMNAL_1982.id}` },
        { name: label, url: pathname },
      ]}
    />
  )
}
